import { useEffect, useRef, useState } from "react";
import { useUser, useSupabaseClient } from "@supabase/auth-helpers-react";
import BREAKPOINTS from "../../utils/breackpoints";
import colors from "../../utils/colors";
import { createNewConversation } from "../../api/chats";
import { toast } from "sonner";
import { IconChevronDown } from "@tabler/icons-react";
import getThemeColors from "../../utils/getColorTheme";
import AccountSettingsModal from "../AccountSettingsModal";
import useAnalytics from "../../hooks/useAnalytics";
import { useTheme } from "../../contexts/ThemeContext";
import { marketingUrl } from "../../utils/origins";


// Icons
import IconSettings from "../../assets/images/icons/iconSettings.svg";
import IconBin from "../../assets/images/icons/iconBin.svg";
import IconBroom from "../../assets/images/icons/iconBroom.svg";
import IconLogout from "../../assets/images/icons/iconLogout.svg";
import IconTerms from "../../assets/images/icons/iconTerms.svg";
const UserHeader = ({ onClick, isLoading }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [clearChatsLoading, setClearChatsLoading] = useState(false);
  const [clearPdfLoading, setClearPdfLoading] = useState(false);
  const user = useUser();
  const supabase = useSupabaseClient();
  const modalRef = useRef(null);
  const dropdownRef = useRef(null);
  const iconRef = useRef(null);
  const headerRef = useRef(null);
  const { trackProfileMenuClick, trackConfirmationAlertClick } = useAnalytics();

  // Calculate initials from the fetched full name
  const nameParts = user?.user_metadata?.full_name?.split(" ");
  const initials = nameParts?.slice(0, 2).map(n => n[0]).join("");
  const isMobile = window.innerWidth <= BREAKPOINTS.LG;
  const { theme, background } = getThemeColors();

  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

  const { addTheme } = useTheme();

  const themeColor =
    addTheme === "dark"
      ? "#F9F5ED"
      : addTheme === "eclipse"
      ? "#16140E"
      : "#16140E";

  const deleteChats = async () => {
    trackProfileMenuClick("clear chat");
    setClearChatsLoading(true);
    try {
      await createNewConversation();
      toast.success("Chats deleted successfully!");
      setIsDropdownOpen(false);
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      toast.error("Error deleting chats");
    } finally {
      setClearChatsLoading(false);
    }
  };

  const clearPdfData = () => {
    trackProfileMenuClick("delete plan");
    toast.message("Do you really want to clear all uploaded PDF data from the AI's memory?", {
      duration: 10000,
      style: { width: 370 },
      action: {
        label: "Yes",
        onClick: () => {
          trackConfirmationAlertClick("plan", true);
          setClearPdfLoading(true);
          try {
            localStorage.setItem("pdf_data_cleared", "true");
            localStorage.setItem("pdf_cleared_timestamp", Date.now().toString());
            toast.success("Removing PDF data from AI memory...");
            setIsDropdownOpen(false);
            window.dispatchEvent(new StorageEvent("storage", {
              key: "pdf_data_cleared",
              newValue: "true",
            }));
            setTimeout(() => setClearPdfLoading(false), 1000);
          } catch (error) {
            toast.error("Error clearing PDF data");
            localStorage.removeItem("pdf_data_cleared");
            localStorage.removeItem("pdf_cleared_timestamp");
            setClearPdfLoading(false);
          }
        },
      },
      cancel: {
        label: "No",
        onClick: () => trackConfirmationAlertClick("plan", false),
      },
    });
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        headerRef.current &&
        !headerRef.current.contains(e.target)
      ) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  const iconStyle = {
    width: "18px",
    height: "18px",
    marginRight: "10px",
  };

  return (
    <div
      ref={headerRef}
      className="d-flex align-items-center position-relative custom-focusable"
      tabIndex={0}
      role="button"
      aria-label="Open user menu"
      style={{ gap: 13 }}
      onClick={toggleDropdown}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggleDropdown();
        }
      }}
    >
      <AccountSettingsModal ref={modalRef} />

      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          overflow: "hidden",
          backgroundColor: colors.yellow,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: isMobile ? "pointer" : "default",
        }}
      >
        <span style={{ color: colors.brown, fontWeight: "bold" }}>{initials}</span>
      </div>

      {!isMobile && (
        <IconChevronDown
          ref={iconRef}
          className={`cursor-pointer ${isDropdownOpen ? "bx-rotate-180" : ""}`}
          size={20}
          style={{
            color: themeColor,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          }}
          onClick={(e) => {
            e.stopPropagation();
            toggleDropdown();
          }}
        />

      )}
      {isDropdownOpen && (
        <div
          ref={dropdownRef}
          className="animate__animated animate__fadeIn animate__faster"
          style={{
            position: "absolute",
            top: isMobile ? 50 : 60,
            right: 0,
            border: `1px solid ${colors.gray}`,
            borderRadius: 8,
            boxShadow: `1px 4px 6px ${
              localStorage.getItem("layoutMode") === "dark"
                ? "rgba(255, 255, 255, 0.2)"
                : "rgba(0, 0, 0, 0.2)"
            }`,
            zIndex: 1000,
            minWidth: 200,
            background: background,
          }}
        >
          <ul className="user-header-dropdown" style={{ listStyle: "none", margin: 0, padding: "10px 15px" }}>
            <li
              tabIndex={0}
              role="menuitem"
              onClick={() => {
                trackProfileMenuClick("account settings");
                modalRef.current?.open();
                setIsDropdownOpen(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  modalRef.current?.open();
                  setIsDropdownOpen(false);
                }
              }}
              style={getItemStyle(theme)}

              onMouseOver={(e) => e.currentTarget.style.textDecoration = "underline"}
              onMouseOut={(e) => e.currentTarget.style.textDecoration = "none"}
            >
              <img src={IconSettings} alt="Settings" style={iconStyle} />
              Account settings
            </li>

            <li
              tabIndex={0}
              role="menuitem"
              onClick={clearPdfLoading ? null : clearPdfData}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && !clearPdfLoading) {
                  e.preventDefault();
                  clearPdfData();
                }
              }}
              style={getItemStyle(theme)}

              onMouseOver={(e) => e.currentTarget.style.textDecoration = "underline"}
              onMouseOut={(e) => e.currentTarget.style.textDecoration = "none"}
            >
              <img src={IconBin} alt="Forget plan" style={iconStyle} />
              {clearPdfLoading ? <LoadingIcon /> : "Forget my plan"}
            </li>

            <li
              tabIndex={0}
              role="menuitem"
              onClick={clearChatsLoading ? null : deleteChats}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && !clearChatsLoading) {
                  e.preventDefault();
                  deleteChats();
                }
              }}
              style={{ ...getItemStyle(theme), borderBottom: `1px solid ${colors.gray}` }}

              onMouseOver={(e) => e.currentTarget.style.textDecoration = "underline"}
              onMouseOut={(e) => e.currentTarget.style.textDecoration = "none"}
            >
              <img src={IconBroom} alt="Clear chats" style={iconStyle} />
              {clearChatsLoading ? <LoadingIcon /> : "Clear chats"}
            </li>

            <li
              tabIndex={0}
              role="menuitem"
              onClick={() => {
                trackProfileMenuClick("terms");
                window.location.href = marketingUrl("/terms");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  window.location.href = marketingUrl("/terms");
                }
              }}
              style={{ ...getItemStyle(theme), whiteSpace: "nowrap" }}

              onMouseOver={(e) => e.currentTarget.style.textDecoration = "underline"}
              onMouseOut={(e) => e.currentTarget.style.textDecoration = "none"}
            >
              <img src={IconTerms} alt="Terms" style={iconStyle} />
              Terms and policies
            </li>

            <li
              tabIndex={0}
              role="menuitem"
              onClick={() => {
                trackProfileMenuClick("logout");
                onClick();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick();
                }
              }}
              style={getItemStyle(theme)}

              onMouseOver={(e) => e.currentTarget.style.textDecoration = "underline"}
              onMouseOut={(e) => e.currentTarget.style.textDecoration = "none"}
            >
              <img src={IconLogout} alt="Logout" style={iconStyle} />
              {isLoading ? <LoadingIcon /> : "Logout"}
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const getItemStyle = (color) => ({
  padding: "8px 0",
  cursor: "pointer",
  color,
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: "5px",
  transition: "all 0.3s ease",
});

const LoadingIcon = () => (
  <span style={{ pointerEvents: "none" }}>
    <i className="bx bx-loader bx-spin fs-5 align-middle" />
  </span>
);

export default UserHeader;
